import React, { useState, useEffect } from "react";
import { LayoutDashboard, MousePointerClick, Package, Activity, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import ProductDetailsModal from "../../../components/ProductDetailsModal";
import DashboardTableModal from "./DashboardModal";
import CatalogDownloadsModal from "./CatalogDownloadsModal";
import { postData1 } from "../../../services";
import ProductInterestModal from "./ProductDetailsModal";
import ProductVisitedList from "./ProductVisitedList";
import UserActivityModal from "../UserActivityModal";



const DashboardDetails = ({ userData }) => {
    // const [showActions, setShowActions] = useState(false);
    const [showProductsModal, setShowProductsModal] = useState(false);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [showPopupUrls, setShowPopupUrls] = useState(false);
    const [showCatalogs, setShowCatalogs] = useState(false);
    // ---- Session summary ----
    const [sessionSummary, setSessionSummary] = useState({ loading: true, count: 0, totalTimeSeconds: 0 });
    const [showSessionModal, setShowSessionModal] = useState(false);
    const [selectedSessionUserName, setSelectedSessionUserName] = useState("");

    // Fetch session data to show count & total time when user loads
    useEffect(() => {
      if (!userData?.uid) return;
      let cancelled = false;

      (async () => {
        try {
          setSessionSummary(prev => ({ ...prev, loading: true }));
          const token = localStorage.getItem("adminAuth");
          const url = `${process.env.REACT_APP_BASEURL}/analytics/analytics-admin/user-sessions/?user=${userData.uid}`;
          const response = await fetch(url, {
            headers: {
              Accept: "application/json",
              Authorization: `Token ${token}`,
            },
          });
          if (cancelled) return;
          if (!response.ok) throw new Error(`Failed: ${response.status}`);
          const data = (await response.json()) || [];
          const count = data.length;
          // Last session = newest (first in array)
          const lastSessionTime = data[0]?.time_spent_seconds || 0;
          if (!cancelled) {
            setSessionSummary({ loading: false, count, totalTimeSeconds: lastSessionTime });
          }
        } catch (err) {
          if (!cancelled) {
            setSessionSummary({ loading: false, count: 0, totalTimeSeconds: 0 });
          }
        }
      })();

      return () => { cancelled = true; };
    }, [userData?.uid]);

    const formatTime = (seconds) => {
      if (!seconds || seconds === 0) return "0s";
      const hrs = Math.floor(seconds / 3600);
      const mins = Math.floor((seconds % 3600) / 60);
      const secs = seconds % 60;
      const parts = [];
      if (hrs > 0) parts.push(`${hrs}h`);
      if (mins > 0) parts.push(`${mins}m`);
      if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);
      return parts.join(" ");
    };

    const [showActions, setShowActions] = useState(false);
    const [actionsData, setActionsData] = useState([]);
    const [actionsLoading, setActionsLoading] = useState(false);
    const [actionsPage, setActionsPage] = useState(1);
    const [actionsTotal, setActionsTotal] = useState(0);
    const [actionsTotalPages, setActionsTotalPages] = useState(1);
    const [actionsPerPage, setActionsPerPage] = useState(10);

    const popupUrls = Array.isArray(userData?.popup_url)
        ? userData.popup_url.map((item) => ({
            product_name: item?.product_name || "",
            product_url: item?.product_url || item?.url || "",
        }))
        : [];

    const handleRecentActionsClick = async () => {
        try {
            setActionsLoading(true);

            const token = localStorage.getItem("adminAuth");
            const response = await fetch(
                `${process.env.REACT_APP_BASEURL}/admin/admin_user_recent_actions/?page=${actionsPage}&page_size=${actionsPerPage}`,
                {
                    method: "POST",
                    headers: {
                        Accept: "application/json",
                        Authorization: `Token ${token}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        uid: userData?.uid,
                    }),
                }
            );

            const result = await response.json();
            setActionsData(result?.results || []);
            setActionsTotal(result?.count || 0);

            setActionsTotalPages(
                Math.ceil((result?.count || 0) / actionsPerPage)
            );

            setShowActions(true);

        } catch (error) {
            console.log(error);
        } finally {
            setActionsLoading(false);
        }
    };

    useEffect(() => {
        if (showActions) {
            handleRecentActionsClick();
        }
    }, [actionsPage, actionsPerPage]);

    const updateActionsPage = (e) => {
        const name = e.target.getAttribute("name");

        if (name === "prev") {
            if (actionsPage > 1) {
                setActionsPage(actionsPage - 1);
            }
        } else if (name === "next") {
            if (actionsPage < actionsTotalPages) {
                setActionsPage(actionsPage + 1);
            }
        } else {
            const page = parseInt(e.target.innerText);

            if (!isNaN(page)) {
                setActionsPage(page);
            }
        }
    };

    const dashboardData = [
        {
            label: "SR. NO",
            value: userData?.id || "-"
        },
        {
            label: "SOURCE",
            value: userData?.source || "Equipmedy"
        },
        {
            label: "ACCOUNT CREATED",
            value: userData?.created_date ? new Date(userData.created_date).toLocaleString("en-IN", {
                day: "numeric",
                month: "numeric",
                year: "numeric",
                hour: "numeric",
                minute: "2-digit",
                second: "2-digit",
                hour12: true,
            }) : "-"
        },
        {
            label: "LAST LOGIN",
            value: userData?.last_login ? new Date(userData.last_login).toLocaleString("en-IN", {
                day: "numeric",
                month: "numeric",
                year: "numeric",
                hour: "numeric",
                minute: "2-digit",
                second: "2-digit",
                hour12: true,
            }) : "-"
        },
        {
            label: "SESSION",
            value: (
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => {
                            setSelectedSessionUserName(userData?.profile?.first_name || userData?.mobile || "");
                            setShowSessionModal(true);
                        }}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-violet-50 text-violet-700 border border-violet-200 hover:bg-violet-100 font-medium transition-all"
                    >
                        <Activity size={16} />
                        {sessionSummary.loading ? (
                            <span className="flex items-center gap-1.5">
                                <span className="w-3 h-3 border-2 border-violet-400 border-t-transparent rounded-full animate-spin"></span>
                                Loading...
                            </span>
                        ) : (
                            <span>{sessionSummary.count} Session{sessionSummary.count !== 1 ? 's' : ''}</span>
                        )}
                    </button>
                    {!sessionSummary.loading && sessionSummary.count > 0 && (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-50 text-amber-700 border border-amber-200 text-sm font-medium" title="Last session duration">
                            <Clock size={14} className="text-amber-500" />
                            {formatTime(sessionSummary.totalTimeSeconds)}
                        </span>
                    )}
                </div>
            )
        },
        {
            label: "RECENT ACTIONS",
            value: (
                <button
                    onClick={handleRecentActionsClick}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 font-medium"
                >
                    <MousePointerClick size={16} />
                    {userData?.user_recent_actions_count || 0} Actions
                </button>
            )
        },
        {
            label: "PRODUCT INTEREST",
            value: (
                <button
                    onClick={() => setShowProductsModal(true)}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 font-medium"
                >
                    <Package size={16} />
                    {userData?.recently_viewed_product_count || 0} Products
                </button>
            )
        },
        {
            label: "POPUP URL",
            value: (
                <button
                    onClick={() => setShowPopupUrls(true)}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-purple-50 text-purple-700 border border-purple-200 hover:bg-purple-100 font-medium"
                >
                    {popupUrls.length} URLs
                </button>
            )
        },
        {
            label: "BROCHURE DOWNLOAD",
            value: (
                <button
                    onClick={() => setShowCatalogs(true)}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-orange-50 text-orange-700 border border-orange-200 hover:bg-orange-100 font-medium"
                >
                    {userData?.catalogs?.length || 0} Catalogs
                </button>
            )
        },
        {
            label: "NEED HELP",
            value: (
                <div className="relative group inline-block">
                    <span className="inline-block min-w-[90px] px-3 py-1 rounded-lg text-sm font-medium border bg-violet-100 text-violet-700 border-violet-300 cursor-pointer text-center">
                        {Object.values(userData?.need_help_stats || {}).reduce(
                            (sum, val) => sum + val,
                            0
                        )} Actions
                    </span>

                    {/* Tooltip */}
                    <div className="absolute left-0 top-full mt-2 z-50 hidden group-hover:block">
                        <div className="bg-white border border-gray-200 rounded-xl shadow-xl p-3 min-w-[240px]">
                            <h4 className="text-sm font-semibold text-gray-700 mb-2 border-b pb-2">
                                Need Help Actions
                            </h4>

                            {Object.entries(
                                userData?.need_help_stats || {}
                            ).map(([action, count]) => (
                                <div
                                    key={action}
                                    className="flex items-center justify-between py-1.5"
                                >
                                    <span className="capitalize text-sm text-gray-600">
                                        {action.replaceAll("_", " ")}
                                    </span>

                                    <span className="px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 text-xs font-medium">
                                        {count}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )
        },
        {
            label: "SM OUTBOUND",
            value: (
                <div className="relative group inline-block">
                    <span className="inline-block min-w-[90px] px-3 py-1 rounded-lg text-sm font-medium border bg-cyan-100 text-cyan-700 border-cyan-300 cursor-pointer text-center">
                        {Object.values(userData?.social_media_outbound_stats || {}).reduce(
                            (sum, val) => sum + val,
                            0
                        )} Clicks
                    </span>

                    <div className="absolute left-0 top-full mt-2 z-50 hidden group-hover:block">
                        <div className="bg-white border border-gray-200 rounded-xl shadow-xl p-3 min-w-[240px]">
                            <h4 className="text-sm font-semibold text-gray-700 mb-2 border-b pb-2">
                                Social Media Outbound
                            </h4>

                            {Object.entries(
                                userData?.social_media_outbound_stats || {}
                            ).map(([platform, count]) => (
                                <div
                                    key={platform}
                                    className="flex items-center justify-between py-1.5"
                                >
                                    <span className="capitalize text-sm text-gray-600">
                                        {platform}
                                    </span>

                                    <span className="px-2 py-0.5 rounded-full bg-cyan-50 text-cyan-700 text-xs font-medium">
                                        {count}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )
        }
    ];




    return (
        <div className="bg-white rounded-[24px] border border-gray-200 shadow-sm overflow-hidden">

            {/* Header */}
            <div className="flex items-center gap-3 px-8 py-6 border-b border-gray-200">
                <div className="w-10 h-10 rounded-lg bg-pink-100 flex items-center justify-center">
                    <LayoutDashboard
                        size={20}
                        className="text-pink-600"
                    />
                </div>

                <h2 className="text-[18px] font-bold uppercase tracking-wide text-slate-800">
                    Dashboard
                </h2>
            </div>


            <div className=" overflow-y-auto px-8 py-4">

                {dashboardData.map((item, index) => (
                    <div
                        key={index}
                        className="py-5 border-b border-gray-200 last:border-b-0"
                    >
                        <p className="text-[13px] font-semibold text-gray-400 uppercase mb-1">
                            {item.label}
                        </p>

                        <div className="text-[16px] font-medium text-slate-900 break-words">
                            {item.value}
                        </div>
                    </div>
                ))}

            </div>
            
            <DashboardTableModal
                isOpen={showActions}
                onClose={() => setShowActions(false)}
                title="Recent Actions"
                data={actionsData}
                loading={actionsLoading}
                columns={[
                    {
                        key: "page_url",
                        label: "Page URL",
                    },
                    {
                        key: "visited_at",
                        label: "Visited At",
                    },
                ]}
                total={actionsTotal}
                perPage={actionsPerPage}
                currPage={actionsPage}
                totalPage={actionsTotalPages}
                updatePage={updateActionsPage}
                setCurrPage={setActionsPage}
                setPerPage={setActionsPerPage}
            />
            <DashboardTableModal
                isOpen={showPopupUrls}
                onClose={() => setShowPopupUrls(false)}
                title="Popup URLs"
                data={popupUrls}
                columns={[
                    {
                        key: "product_name",
                        label: "Product Name",
                    },
                    {
                        key: "product_url",
                        label: "URL",
                    },
                ]}
            />
            <ProductInterestModal
                isOpen={showProductsModal}
                onClose={() => setShowProductsModal(false)}
            >
                <ProductVisitedList
                    userUid={userData?.uid}
                />
            </ProductInterestModal>

            <CatalogDownloadsModal
                isOpen={showCatalogs}
                onClose={() => setShowCatalogs(false)}
                catalogs={userData?.catalogs || []}
            />

            <UserActivityModal
                isOpen={showSessionModal}
                onClose={() => {
                    setShowSessionModal(false);
                    setSelectedSessionUserName("");
                }}
                userUid={userData?.uid}
                userName={selectedSessionUserName}
            />
        </div>
    );
};

export default DashboardDetails;